/**
 * Undo/redo for the motif. Operations are small and invertible; the raster is rebuilt from the
 * stroke list after an undo or redo.
 */
import { type Motif, type Stroke, cloneMotif } from './motif';

type Op =
  | { kind: 'add'; strokes: Stroke[] }
  | { kind: 'replace'; before: Stroke[]; after: Stroke[]; label: string };

export class MotifHistory {
  private motif: Motif;
  private undoStack: Op[] = [];
  private redoStack: Op[] = [];
  private readonly limit: number;

  constructor(initial: Motif, limit = 200) {
    this.motif = cloneMotif(initial);
    this.limit = limit;
  }

  get current(): Motif {
    return this.motif;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  private push(op: Op): void {
    this.undoStack.push(op);
    if (this.undoStack.length > this.limit) this.undoStack.shift();
    this.redoStack = [];
  }

  /** Appends the pieces of one stroke as a single undoable step. */
  add(strokes: Stroke[]): void {
    if (strokes.length === 0) return;
    this.motif.strokes.push(...strokes);
    this.push({ kind: 'add', strokes });
  }

  /** Replaces the whole motif (clear, load default, load from a link). */
  replace(next: Motif, label: string): void {
    const before = [...this.motif.strokes];
    const after = cloneMotif(next).strokes;
    this.motif = { strokes: [...after] };
    this.push({ kind: 'replace', before, after, label });
  }

  /** Resets without an undo entry (initial load). */
  reset(next: Motif): void {
    this.motif = cloneMotif(next);
    this.undoStack = [];
    this.redoStack = [];
  }

  /** Returns a short description of what was undone, or null. */
  undo(): string | null {
    const op = this.undoStack.pop();
    if (!op) return null;
    if (op.kind === 'add') {
      this.motif.strokes.splice(this.motif.strokes.length - op.strokes.length, op.strokes.length);
    } else {
      this.motif = { strokes: [...op.before] };
    }
    this.redoStack.push(op);
    return op.kind === 'add' ? 'stroke' : op.label;
  }

  redo(): string | null {
    const op = this.redoStack.pop();
    if (!op) return null;
    if (op.kind === 'add') this.motif.strokes.push(...op.strokes);
    else this.motif = { strokes: [...op.after] };
    this.undoStack.push(op);
    return op.kind === 'add' ? 'stroke' : op.label;
  }
}
